import {BIService} from '../services/BIService';
import {CartStore} from '../stores/CartStore';
import {CashierExpressStore} from '../stores/CashierExpressStore';
import {
  FetchContext,
  ICartControllerApi,
  ICartStyleSettings,
  IControllerProps,
  VeloInputs,
} from '../../types/app.types';
import {NavigationStore} from '../stores/NavigationStore';
import {OrderStore} from '../stores/OrderStore';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {NavigationService} from '../services/NavigationService';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {CartService} from '../services/CartService';
import {ProductsService} from '../services/ProductsService';
import {OrderService} from '../services/OrderService';
import {
  CheckoutNavigationService,
  MultilingualService,
  BaseController,
  FedopsInteractions,
  SiteStore,
} from '@wix/wixstores-client-storefront-sdk';
import {StoreMetaDataService} from '../services/StoreMetaDataService';
import {StoreSettingsService} from '../services/StoreSettingsService';
import {ModalManagerService} from '../services/ModalManagerService';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {APP_DEFINITION_ID} from '@wix/wixstores-client-core';
import {applyDefaults} from '../utils/applyDefaults';

export abstract class BaseCartController extends BaseController {
  protected abstract getOrigin(): string;
  protected abstract getDefaultSettings(): ICartStyleSettings;

  private readonly services = {} as {
    biService: BIService;
    storeSettingsService: StoreSettingsService;
    cartService: CartService;
    navigationService: NavigationService;
    modalManagerService: ModalManagerService;
    orderService: OrderService;
    productsService: ProductsService;
    styleSettingsService: StyleSettingsService;
    checkoutNavigationService: CheckoutNavigationService;
    multilingualService: MultilingualService;
    storeMetaDataService: StoreMetaDataService;
    minimumOrderAmountService: MinimumOrderAmountService;
  };
  private veloInputs: VeloInputs;
  private isControllerLoaded: boolean;
  public stores = {} as {
    navigation: NavigationStore;
    cart: CartStore;
    order: OrderStore;
    cashierExpress: CashierExpressStore;
  };

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    this.isControllerLoaded = false;
    this.initDefaultVeloInputs();
    const styleWithDefaults = applyDefaults(
      this.getDefaultSettings(),
      controllerParams.controllerConfig.config.style.styleParams as unknown as ICartStyleSettings
    );
    this.subscribeToChanges();
    this.setServices(styleWithDefaults, controllerParams);
    this.setStores(controllerParams);
  }

  private initDefaultVeloInputs(): void {
    this.veloInputs = {shouldShowSecureCheckout: true};
  }

  /* istanbul ignore next */
  public getSiteStore(): SiteStore {
    return this.siteStore;
  }

  public readonly updateComponent = async () => {
    this.setProps({
      cartStore: await this.stores.cart.toProps(),
      cashierExpressStore: this.stores.cashierExpress.toProps(),
      navigationStore: await this.stores.navigation.toProps(),
      orderStore: this.stores.order.toProps(),
      isLoading: false,
      isResponsive: this.services.styleSettingsService.isEditorX,
      fitToContentHeight: true,
    } as IControllerProps);
  };

  public setVeloInputs = async (veloInputs: VeloInputs) => {
    this.veloInputs = {
      ...this.veloInputs,
      ...veloInputs,
    };
    this.stores.cart.setOverrides(this.veloInputs);
    if (this.isControllerLoaded) {
      await this.updateComponent();
    }
  };

  protected readonly reportFedopsInteraction = async (interaction: FedopsInteractions, fn: Function) => {
    this.flowAPI.fedops.interactionStarted(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).start();
    await fn();
    this.flowAPI.fedops.interactionEnded(interaction);
    this.flowAPI.panoramaClient?.transaction(interaction).finish();
  };

  private get controllerApi(): ICartControllerApi {
    return {
      executeWithLoader: this.executeWithLoader,
      updateComponent: this.updateComponent,
      reportFedopsInteraction: this.reportFedopsInteraction,
      t: this.t,
    };
  }

  public readonly load = async (loadContext?: string): Promise<void> => {
    this.setProps({isLoading: true});
    await this.services.cartService.fetchCart(loadContext).then(this.updateComponent);
  };

  private readonly warmup = async () => {
    await Promise.all([
      this.services.cartService.fetchCart(FetchContext.Init).then(() => {
        this.stores.cashierExpress.init();
      }),
      this.services.storeSettingsService.fetch(),
    ]);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sendCartLoadedBi();
    await this.updateComponent();
    this.isControllerLoaded = true;
  };

  public readonly init = async (): Promise<void> => {
    return this.warmup();
  };

  private async sendCartLoadedBi() {
    return this.services.biService.viewCartPageSf({
      cart: this.services.cartService.cart,
      cartType: this.services.cartService.cartType,
      isEligibleForCheckoutInViewer: await this.services.checkoutNavigationService.isEligibleForCheckoutInViewer(),
      paymentMethods: (await this.services.storeMetaDataService.get()).activePaymentMethods || [],
      shouldShowExpressCheckout: this.services.styleSettingsService.shouldShowExpressCheckout,
      numOfVisibleShippingOptions: Math.min(this.services.orderService.shippingRuleOptions.length, 2),
      shouldShowCoupon: this.services.styleSettingsService.shouldShowCoupon,
      shouldShowBuyerNote: this.services.styleSettingsService.shouldShowBuyerNote,
      shouldShowContinueShopping: this.services.styleSettingsService.shouldRenderContinueShopping,
      shouldShowShipping: this.services.styleSettingsService.shouldShowShipping,
      shouldShowTax: this.services.styleSettingsService.shouldShowTax,
      hasPickupOption: this.services.orderService.hasPickupOption,
    });
  }

  private readonly executeWithLoader = async (fn: Function, fetchContext: FetchContext) => {
    this.setProps({isLoading: true});
    try {
      await fn();
      await this.load(fetchContext);
    } finally {
      this.setProps({isLoading: false});
    }
  };

  public readonly onStyleUpdate = async (styleParams: {}): Promise<void> => {
    this.services.styleSettingsService.update(
      applyDefaults(this.getDefaultSettings(), styleParams as unknown as ICartStyleSettings)
    );
    await this.updateComponent();
  };

  private subscribeToChanges() {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.platformAPIs.pubSub.subscribe(
      'Cart.Changed',
      (data: {data: {reason: string}}) => this.load(data?.data?.reason),
      false
    );

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.siteStore.siteApis.onInstanceChanged(/* istanbul ignore next */ () => this.init(), APP_DEFINITION_ID);
  }

  private setServices(styleSettings: ICartStyleSettings, controllerParams: ControllerParams) {
    this.services.modalManagerService = new ModalManagerService({siteStore: this.siteStore}, controllerParams);
    this.services.styleSettingsService = new StyleSettingsService(styleSettings);
    this.services.biService = new BIService({siteStore: this.siteStore});
    this.services.storeSettingsService = new StoreSettingsService({siteStore: this.siteStore});
    this.services.cartService = new CartService({
      controllerApi: this.controllerApi,
      siteStore: this.siteStore,
      biService: this.services.biService,
      styleSettingsService: this.services.styleSettingsService,
    });
    this.services.productsService = new ProductsService({siteStore: this.siteStore});
    this.services.orderService = new OrderService({
      cartService: this.services.cartService,
      styleSettingsService: this.services.styleSettingsService,
      storeSettingsService: this.services.storeSettingsService,
    });
    this.services.minimumOrderAmountService = new MinimumOrderAmountService({
      orderService: this.services.orderService,
      styleSettingsService: this.services.styleSettingsService,
    });

    const mergedPublicData = {...this.publicData.APP, ...this.publicData.COMPONENT};
    this.services.multilingualService = new MultilingualService(
      this.siteStore,
      mergedPublicData,
      {} // todo: fetch appSettings and pass here
    );

    this.services.storeMetaDataService = new StoreMetaDataService({siteStore: this.siteStore});
    this.services.checkoutNavigationService = new CheckoutNavigationService({
      siteStore: this.siteStore,
      origin: this.getOrigin(),
    });
    this.services.navigationService = new NavigationService({
      siteStore: this.siteStore,
      biService: this.services.biService,
      cartService: this.services.cartService,
      publicData: this.publicData,
      controllerApi: this.controllerApi,
      modalManagerService: this.services.modalManagerService,
    });
  }

  private setStores(controllerParams: ControllerParams) {
    this.stores.cart = new CartStore(this.controllerApi, this.siteStore, this.services, this.veloInputs);
    this.stores.order = new OrderStore(this.controllerApi, this.siteStore, this.services);
    this.stores.navigation = new NavigationStore(this.siteStore, this.services);
    this.stores.cashierExpress = new CashierExpressStore(
      controllerParams,
      this.controllerApi,
      this.siteStore,
      this.services,
      this.services.styleSettingsService
    );
  }
}
